import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { getRequest } from "@tanstack/react-start/server";
import { createHash } from "crypto";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

const contactSchema = z.object({
  inquiry_type: z.string().trim().min(1).max(60).default("General"),
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
  message: z.string().trim().min(5).max(4000),
  budget: z.string().trim().max(120).optional().or(z.literal("")).transform((v) => (v ? v : undefined)),
  timeline: z.string().trim().max(120).optional().or(z.literal("")).transform((v) => (v ? v : undefined)),
  // Honeypot — bots often fill hidden fields.
  website: z.string().max(0).optional().or(z.literal("")).optional(),
});

function serverClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  const url = process.env.SUPABASE_URL!;
  const isNew = key.startsWith("sb_publishable_") || key.startsWith("sb_secret_");
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (isNew && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

function hashIp(): string | null {
  try {
    const req = getRequest();
    const ip =
      req?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      req?.headers.get("cf-connecting-ip") ||
      req?.headers.get("x-real-ip") ||
      "";
    if (!ip) return null;
    return createHash("sha256").update(ip + "|buddhaka-contact").digest("hex").slice(0, 32);
  } catch {
    return null;
  }
}

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => contactSchema.parse(data))
  .handler(async ({ data }) => {
    // Honeypot: silently accept and drop.
    if (data.website && data.website.length > 0) {
      return { ok: true, forwarded: false as const };
    }

    const supabase = serverClient();
    const ip_hash = hashIp();

    if (ip_hash) {
      const { checkRateLimit } = await import("@/lib/rate-limit.server");
      const allowed = await checkRateLimit(`contact:${ip_hash}`, 5, 3600);
      if (!allowed) throw new Error("Too many submissions. Please try again later.");
    }


    const { error } = await supabase
      .from("contact_submissions")
      .insert({
        inquiry_type: data.inquiry_type,
        name: data.name,
        email: data.email,
        message: data.message,
        budget: data.budget ?? null,
        timeline: data.timeline ?? null,
        ip_hash,
      });

    if (error) {
      // eslint-disable-next-line no-console
      console.error("[submitContact] insert failed", JSON.stringify(error));
      throw new Error("Unable to submit. Please try again.");
    }

    // TODO(email-forwarding): forward new submissions to BuddhaKA9@gmail.com via
    // Lovable managed email templates. Requires an email domain to be verified
    // for buddhaka.com (see admin Cloud → Emails). Until then, submissions are
    // stored and visible in /admin/contact.
    return { ok: true, forwarded: false as const };
  });
